<template>
  <SalonSelectLocation
    v-if="stepName === 'location'"
    @selected="onOutletSelected"
    @bookings="onOpenBookingsFromLanding"
  />

  <SalonLogin
    v-else-if="stepName === 'login'"
    @back="stepName = 'location'"
    @logged-in="onLoggedIn"
    @register="onNeedsRegistration"
  />

  <SalonRegister
    v-else-if="stepName === 'register'"
    :phone-number="phoneNumber"
    :phone-code="phoneCode"
    @back="stepName = 'login'"
    @registered="onRegistered"
  />

  <SalonSelectService
    v-else-if="stepName === 'service'"
    :branch-code="branchCode"
    :branch-name="branchName"
    :show-price="showServicePrice"
    :member-initials="memberInitials"
    :member-name="memberName"
    @back="stepName = stepBeforeService"
    @selected="onServicesSelected"
    @profile="onProfileNavigate"
  />

  <SalonSelectDate
    v-else-if="stepName === 'date'"
    :branch-code="branchCode"
    :branch-name="branchName"
    :services="selectedServices"
    :initial-date="selectedDate"
    :show-price="showServicePrice"
    :staff-id="isStaffFirst ? selectedStaffId : ''"
    :staff-name="isStaffFirst ? namedStylist : ''"
    :step="stepNumbers.date"
    :flow-toggle="canSwitchFlow && !isStaffFirst"
    :member-initials="memberInitials"
    :member-name="memberName"
    @back="stepName = isStaffFirst ? 'staff' : 'service'"
    @selected="onDateSelected"
    @switch-flow="onSwitchFlow"
    @profile="onProfileNavigate"
  />

  <SalonSelectTime
    v-else-if="stepName === 'time'"
    :branch-code="branchCode"
    :date="selectedDate"
    :date-text="selectedDateText"
    :duration-min="durationMin"
    :services="selectedServices"
    :initial-time="selectedTime"
    :staff-id="isStaffFirst ? selectedStaffId : ''"
    :staff-name="isStaffFirst ? namedStylist : ''"
    :step="stepNumbers.time"
    :next-label="isStaffFirst ? $t('salon.flow.reviewBooking') : $t('salon.flow.continue')"
    :member-initials="memberInitials"
    :member-name="memberName"
    @back="stepName = 'date'"
    @selected="onTimeSelected"
    @profile="onProfileNavigate"
  />

  <SalonSelectStaff
    v-else-if="stepName === 'staff'"
    :branch-code="branchCode"
    :date="isStaffFirst ? '' : selectedDate"
    :time="isStaffFirst ? '' : selectedTime"
    :duration-min="durationMin"
    :services="selectedServices"
    :initial-staff-id="selectedStaffId"
    :step="stepNumbers.staff"
    :next-label="isStaffFirst ? $t('salon.flow.continue') : $t('salon.flow.reviewBooking')"
    :flow-toggle="canSwitchFlow && isStaffFirst"
    :member-initials="memberInitials"
    :member-name="memberName"
    @back="stepName = isStaffFirst ? 'service' : 'time'"
    @selected="onStaffSelected"
    @switch-flow="onSwitchFlow"
    @profile="onProfileNavigate"
  />

  <SalonReviewBooking
    v-else-if="stepName === 'review'"
    :branch-code="branchCode"
    :branch-name="branchName"
    :address-text="addressText"
    :postal-code="postalCode"
    :services="selectedServices"
    :date="selectedDate"
    :date-text="selectedDateText"
    :time="selectedTime"
    :time-text="selectedTimeText"
    :duration-min="durationMin"
    :staff-id="selectedStaffId"
    :staff-name="selectedStaffName"
    :promocode-id="promocodeId"
    :promocode-name="promocodeName"
    :promocode-discount="promocodeDiscount"
    :show-price="showServicePrice"
    :member-initials="memberInitials"
    :member-name="memberName"
    @back="stepName = isStaffFirst ? 'time' : 'staff'"
    @edit="onEditFromReview"
    @promo="onOpenPromo"
    @remove-promo="clearPromo"
    @created="onBookingCreated"
    @profile="onProfileNavigate"
  />

  <SalonSelectPromoCode
    v-else-if="stepName === 'promo'"
    key="promo"
    :member-id="memberId"
    :branch-code="branchCode"
    :services="selectedServices"
    :subtotal="subtotal"
    :applied-voucher-id="promocodeVoucherId"
    :member-initials="memberInitials"
    :member-name="memberName"
    @back="stepName = 'review'"
    @applied="onPromoApplied"
    @removed="onPromoRemoved"
    @profile="onProfileNavigate"
  />

  <!-- SH016 — "Awards" is the same voucher list with nothing to apply it to: the same
       screen the booking uses, told to browse rather than pick. Keyed apart from it so
       Vue mounts a fresh one instead of patching the picker's selection into it. -->
  <SalonSelectPromoCode
    v-else-if="stepName === 'awards'"
    key="awards"
    browse
    :member-id="memberId"
    :member-initials="memberInitials"
    :member-name="memberName"
    @back="stepName = returnStep"
    @profile="onProfileNavigate"
  />

  <!-- SH024 — the customer's own record. A detour like Awards and Bookings, so Back goes
       back to whatever they were doing rather than to a fixed step. -->
  <SalonProfile
    v-else-if="stepName === 'profile'"
    :member-initials="memberInitials"
    :member-name="memberName"
    :phone-number="phoneNumber"
    :phone-code="phoneCode"
    @back="stepName = returnStep"
    @updated="onProfileUpdated"
    @profile="onProfileNavigate"
  />

  <!-- SH044 — "Stylist vote": the team at this sale channel, five stars each. A detour
       like Profile and Awards, so Back returns to whatever the customer was doing. -->
  <SalonStylistVote
    v-else-if="stepName === 'vote'"
    :branch-code="branchCode"
    :branch-name="branchName"
    :member-initials="memberInitials"
    :member-name="memberName"
    @back="stepName = returnStep"
    @profile="onProfileNavigate"
  />

  <SalonAppointmentCreated
    v-else-if="stepName === 'created' && booking"
    :booking="booking"
    :destination-link="destinationLink"
    :show-price="showServicePrice"
    :member-initials="memberInitials"
    :member-name="memberName"
    @cancelled="onBookingCancelled"
    @my-bookings="onOpenBookings"
    @book-another="onBookAnother"
    @profile="onProfileNavigate"
  />

  <SalonMyBookings
    v-else-if="stepName === 'bookings'"
    :show-price="showServicePrice"
    :member-initials="memberInitials"
    :member-name="memberName"
    @back="stepName = returnStep"
    @open="onOpenBooking"
    @rate="onRateBooking"
    @book-another="onBookAnother"
    @profile="onProfileNavigate"
  />

  <SalonRateStylist
    v-else-if="stepName === 'rating' && ratingBooking"
    :booking="ratingBooking"
    @back="stepName = 'bookings'"
    @rated="onBookingRated"
  />

  <SalonWelcomeBack
    v-else
    :phone-number="phoneNumber"
    :user="user"
    :member-initials="memberInitials"
    :member-name="memberName"
    @back="stepName = 'login'"
    @switch-number="onSwitchNumber"
    @continue="onContinueBooking"
    @profile="onProfileNavigate"
  />
</template>

<script>
import SalonSelectLocation from "@/salon/views/SelectLocation.vue";
import SalonLogin from "@/salon/views/Login.vue";
import SalonRegister from "@/salon/views/Register.vue";
import SalonWelcomeBack from "@/salon/views/WelcomeBack.vue";
import SalonSelectService from "@/salon/views/SelectService.vue";
import SalonSelectDate from "@/salon/views/SelectDate.vue";
import SalonSelectTime from "@/salon/views/SelectTime.vue";
import SalonSelectStaff from "@/salon/views/SelectStaff.vue";
import SalonReviewBooking from "@/salon/views/ReviewBooking.vue";
import SalonSelectPromoCode from "@/salon/views/SelectPromoCode.vue";
import SalonAppointmentCreated from "@/salon/views/AppointmentCreated.vue";
import SalonMyBookings from "@/salon/views/MyBookings.vue";
import SalonProfile from "@/salon/views/Profile.vue";
import SalonRateStylist from "@/salon/views/RateStylist.vue";
import SalonStylistVote from "@/salon/views/StylistVote.vue";
import TokenService from "@/services/token.service";

/**
 * SH001-SH002 — the salon booking flow's first steps.
 *
 * The salon screens are steps of one linear flow rather than pages of their own: they are
 * rendered by A&PCover in place of the MOO landing page, share the same URL, and the step
 * lives here in state. Keeping the URL still is deliberate — the branch_code in it is the
 * QR code's, and rewriting it mid-flow would re-run App.vue's branch check and
 * checkUrlExpiry against a channel the customer has not committed to yet.
 */
// SH016/SH024/SH044 — steps reached sideways from the profile menu rather than by walking
// the booking forward. They share one `returnStep`, so moving between them must not
// overwrite the screen that is really behind them.
const DETOUR_STEPS = ["awards", "bookings", "profile", "vote"];

// SH026 — minty_branches.salon_booking_flow, the sale channel's answer to "what does the
// customer pick first?". DATE_FIRST is the order MOO ran before the setting existed.
const FLOW_DATE_FIRST = 1;
const FLOW_STAFF_FIRST = 2;
const FLOW_TOGGLE = 3;

export default {
  name: "SalonBookingFlow",
  components: {
    SalonSelectLocation,
    SalonLogin,
    SalonRegister,
    SalonWelcomeBack,
    SalonSelectService,
    SalonSelectDate,
    SalonSelectTime,
    SalonSelectStaff,
    SalonReviewBooking,
    SalonSelectPromoCode,
    SalonAppointmentCreated,
    SalonMyBookings,
    SalonProfile,
    SalonRateStylist,
    SalonStylistVote,
  },
  data() {
    return {
      stepName: "location",
      // SH017 — the past appointment being rated. Held here rather than passed through the
      // route, because the whole booking travels: the rating screen needs the stylist's name
      // and the booking code, and My bookings already has both.
      ratingBooking: null,
      // Registration skips Welcome back, so Back out of the service picker has to return
      // to whichever screen actually sent the customer there.
      stepBeforeService: "welcome",
      // SH026 — the outlet's setting, and the order actually being walked. They are two
      // facts rather than one: on a "let the customer switch" channel the setting stays 3
      // while `flowOrder` follows whichever half of the toggle is showing.
      bookingFlow: FLOW_DATE_FIRST,
      flowOrder: "date",
      // SH032 — minty_branches.salon_show_service_price. Off unless the outlet asks for
      // prices: a colour is quoted at the chair, and a number on the phone the salon
      // cannot honour is worse than no number at all.
      showServicePrice: false,
      branchCode: "",
      branchName: "",
      // SH009/SH010 — the outlet's address for the review card and the ticket, and the map
      // link behind "Get directions". Carried from SH001 rather than re-fetched: the branch
      // endpoint is a physical-branch read, and by this point the flow has left it behind.
      branchId: 0,
      addressText: "",
      postalCode: "",
      destinationLink: "",
      phoneNumber: "",
      phoneCode: "65",
      user: null,
      selectedServices: [],
      // SH006 — the day the customer picked, and the whole availability row behind it
      // so SH007 can read the outlet's hours for that day without asking again.
      selectedDate: "",
      selectedDateText: "",
      selectedDay: null,
      durationMin: 0,
      // SH007 — the start of the appointment, once the customer has picked a slot.
      selectedTime: "",
      selectedTimeText: "",
      // SH008 — the stylist, or 0 for "No Preference". M925's calendar event stores the
      // name rather than the id, so the name travels too.
      selectedStaffId: "",
      selectedStaffName: "",
      // SH009 — the voucher the customer picked on the promo screen. The API clamps the
      // amount to the subtotal, so this is only what the review screen previews.
      promocodeId: "",
      // A member can own several copies of one reward, so which copy was spent is its own
      // fact — the reward id is shared by all of them.
      promocodeVoucherId: "",
      promocodeCode: "",
      promocodeName: "",
      promocodeDiscount: 0,
      // SH010 — the confirmed appointment, as the API returned it.
      booking: null,
      // SH016/SH024 — Profile, Awards and My bookings are opened from the profile menu,
      // from whichever screen the customer happened to be on. Back has to put them back
      // there rather than on a fixed step, or the menu becomes a way to lose a half-made
      // booking.
      returnStep: "service",
    };
  },
  watch: {
    /**
     * Every salon screen is a step of this one component, so they all share the app
     * shell's single scroll container (`main.main-content`) — and that container keeps its
     * offset when the step swaps. Confirming a booking is the worst case: it is reached by
     * scrolling to the bottom of a long review page, so SH010 would open halfway down its
     * own page with "You're booked" already scrolled past.
     *
     * A route change would have done this for free; a step change has to ask.
     */
    stepName() {
      this.$nextTick(this.scrollToTop);
    },
  },
  computed: {
    // SH026 — whether the stylist comes before the date right now.
    isStaffFirst() {
      return this.flowOrder === "staff";
    },

    // Only the third setting offers the switch; the other two are the merchant's decision.
    canSwitchFlow() {
      return Number(this.bookingFlow) === FLOW_TOGGLE;
    },

    // Which of the nine each of the reordered three is. The count never changes — the same
    // questions in a different order — so only the trio between the service and the review
    // swaps places, and the progress bar stays honest either way.
    stepNumbers() {
      return this.isStaffFirst
        ? { staff: 6, date: 7, time: 8 }
        : { date: 6, time: 7, staff: 8 };
    },

    // The stylist's own name, or "" for No Preference — which names nobody, so the date
    // and time screens have no calendar to point at.
    namedStylist() {
      return Number(this.selectedStaffId) > 0 ? this.selectedStaffName : "";
    },

    // The voucher rules are priced against the member, and the salon flow already knows
    // who that is from SH002/SH003 — no need to reach back into localStorage for it.
    memberId() {
      return this.user?.id ?? "";
    },

    // SH016 — who the avatar in the top bar stands for. The login response is the only
    // name the flow is given; WelcomeBack's richer summary is that screen's own call.
    //
    // SH024 — both halves, not just `name`. Salon registration writes the whole name into
    // `name`, but the profile screen lets the customer split it properly, and a member who
    // did that would otherwise be greeted by their first name alone from then on.
    memberName() {
      if (!this.user) {
        return "";
      }

      return [this.user.name, this.user.last_name]
        .map((part) => String(part || "").trim())
        .filter(Boolean)
        .join(" ");
    },

    // First and last initial, the way the reference draws it ("JT"). A one-word name has
    // no last initial to take, so it lends its own second letter instead.
    memberInitials() {
      const parts = this.memberName.trim().split(/\s+/).filter(Boolean);

      if (!parts.length) {
        return "";
      }

      if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
      }

      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    },

    // What the voucher is measured against. Mirrors ReviewBooking's own subtotal: every
    // service plus its add-ons, before any discount or GST.
    subtotal() {
      return this.selectedServices.reduce((total, service) => {
        const addOns = (service.add_ons || []).reduce(
          (sum, addOn) => sum + Number(addOn.price || 0),
          0
        );

        return total + Number(service.price || 0) + addOns;
      }, 0);
    },
  },
  methods: {
    scrollToTop() {
      // The shell's container is what actually scrolls; the window is reset too because
      // the kiosk and physical-branch shells do not mount `.main-content` at all.
      const container = document.querySelector("main.main-content");

      if (container) {
        container.scrollTop = 0;
      }

      window.scrollTo(0, 0);
    },

    onOutletSelected(payload) {
      this.branchCode = (payload && payload.branch_code) || "";
      this.branchName = (payload && payload.branch_name) || "";
      this.branchId = (payload && payload.branch_id) || 0;
      this.addressText = (payload && payload.address_text) || "";
      this.postalCode = (payload && payload.postal_code) || "";
      this.destinationLink = (payload && payload.destination_link) || "";

      // SH026 — the outlet decides the order, so it is read here rather than at the step
      // that uses it: a customer who goes back and picks a different outlet gets that
      // outlet's flow, not the one they started under.
      this.bookingFlow = Number(payload && payload.salon_booking_flow) || FLOW_DATE_FIRST;
      // A "let the customer switch" channel opens on the date, the way MOO always has —
      // the toggle is an offer, not a question the customer has to answer first.
      this.flowOrder = this.bookingFlow === FLOW_STAFF_FIRST ? "staff" : "date";

      // SH032 — read here for the same reason as the flow: a customer who goes back and
      // picks a different outlet gets that outlet's answer, not the one they started under.
      this.showServicePrice =
        Number(payload && payload.salon_show_service_price) === 1;

      // A customer who is still signed in from a previous booking has nothing to type;
      // Welcome back confirms who they are and lets them switch if it is not them.
      if (this.isMemberSignedIn()) {
        const member = TokenService.getMember();
        this.user = member?.data?.user ?? null;
        this.phoneNumber = this.user?.phone_number ?? "";
        this.stepName = "welcome";
        return;
      }

      this.stepName = "login";
    },

    isMemberSignedIn() {
      try {
        return !!TokenService.getLocalAccessToken();
      } catch (error) {
        // getLocalAccessToken() reaches through user.data.authorisation without guarding
        // each hop, so a half-written member blob throws rather than returning null.
        return false;
      }
    },

    onLoggedIn(payload) {
      this.user = (payload && payload.user) || null;
      this.phoneNumber = (payload && payload.phone_number) || "";
      this.phoneCode = (payload && payload.phone_code) || "65";
      this.stepName = "welcome";
    },

    // SH003 — no account behind this number yet, so the customer names themselves and
    // picks a PIN. The number carries over: they have already typed it once.
    onNeedsRegistration(payload) {
      this.phoneNumber = (payload && payload.phone_number) || "";
      this.phoneCode = (payload && payload.phone_code) || "65";
      this.stepName = "register";
    },

    // A brand-new member has no visit history, so the Welcome back card would greet them
    // with nothing. The flowchart sends registration straight on to the booking.
    onRegistered(payload) {
      this.user = (payload && payload.user) || null;
      this.phoneNumber = (payload && payload.phone_number) || this.phoneNumber;
      this.onContinueBooking();
    },

    onSwitchNumber() {
      this.user = null;
      this.phoneNumber = "";
      this.stepName = "login";
    },

    // SH004 — the service picker takes over from here. It lists the outlet's menu set,
    // so the flow stays on this URL instead of handing over to the MOO ordering page.
    onContinueBooking() {
      this.stepBeforeService = this.stepName === "register" ? "login" : "welcome";
      this.stepName = "service";
    },

    // SH006 — the services are settled, so the flow moves on to whichever question this
    // channel asks first: the day, or the stylist.
    onServicesSelected(payload) {
      this.selectedServices = (payload && payload.services) || [];
      // SH009 — a voucher was priced against the old basket, and the ordering API's rules
      // restrict some vouchers to particular items. Carrying the discount over would show
      // a total the API is about to disagree with.
      this.clearPromo();
      this.stepName = this.isStaffFirst ? "staff" : "date";
    },

    // SH007 — the day is settled. The time follows it either way; what changes is what
    // came before.
    onDateSelected(payload) {
      this.selectedDate = (payload && payload.date) || "";
      this.selectedDateText = (payload && payload.date_text) || "";
      this.selectedDay = (payload && payload.day) || null;
      this.durationMin = (payload && payload.duration_min) || 0;
      this.stepName = "time";
    },

    // SH008 — the time is settled: the stylist next on a date-first channel, the review on
    // a staff-first one, where the stylist was chosen before any of this.
    onTimeSelected(payload) {
      this.selectedTime = (payload && payload.time) || "";
      this.selectedTimeText = (payload && payload.time_text) || "";
      this.stepName = this.isStaffFirst ? "review" : "staff";
    },

    // SH009 — the stylist is settled: the review on a date-first channel, the date picker
    // on a staff-first one, where the calendar is theirs from here on.
    onStaffSelected(payload) {
      const staffId = payload ? payload.staff_id : "";
      // SH026 — a different stylist means a different calendar, so a day and time picked
      // under the previous one cannot be carried over.
      const changed = String(staffId) !== String(this.selectedStaffId);

      this.selectedStaffId = staffId;
      this.selectedStaffName = (payload && payload.staff_name) || "";

      if (this.isStaffFirst) {
        if (changed) {
          this.clearMoment();
        }

        this.stepName = "date";
        return;
      }

      this.stepName = "review";
    },

    /**
     * SH026 — the day and time, forgotten.
     *
     * Both belong to whichever stylist and order they were chosen under; keeping them
     * across a change to either would show the review screen a slot nobody has re-checked.
     */
    clearMoment() {
      this.selectedDate = "";
      this.selectedDateText = "";
      this.selectedDay = null;
      this.selectedTime = "";
      this.selectedTimeText = "";
    },

    /**
     * SH026 — the segmented toggle at the top of the first step, on a channel that lets
     * the customer choose the order.
     *
     * Everything picked so far in this leg goes with the switch: a date chosen against the
     * whole channel is not a date this stylist can necessarily take, and a stylist chosen
     * without a moment was never checked against one. The services and the outlet stay —
     * the customer is changing how they book, not what.
     */
    onSwitchFlow(order) {
      const next = order === "staff" ? "staff" : "date";

      if (!this.canSwitchFlow || next === this.flowOrder) {
        return;
      }

      this.flowOrder = next;
      this.clearMoment();
      this.selectedStaffId = "";
      this.selectedStaffName = "";
      this.stepName = next;
    },

    /**
     * SH009 — the Edit links on the review card.
     *
     * Each one jumps straight back to the step that owns that fact rather than walking the
     * flow backwards, and everything chosen after it is cleared on the way — otherwise
     * Review would come back showing a slot nobody has re-checked.
     *
     * SH026 — "after it" depends on the order the channel runs. Date-first, the stylist was
     * chosen against a date and a time, so changing either forgets them; staff-first, the
     * date and time were chosen against the stylist, so changing the stylist forgets those.
     */
    onEditFromReview(step) {
      if (this.isStaffFirst) {
        if (step === "staff") {
          this.clearMoment();
        }

        if (step === "date") {
          this.selectedTime = "";
          this.selectedTimeText = "";
        }

        this.stepName = step;
        return;
      }

      if (step === "date") {
        this.selectedTime = "";
        this.selectedTimeText = "";
      }

      if (step === "date" || step === "time") {
        this.selectedStaffId = "";
        this.selectedStaffName = "";
      }

      this.stepName = step;
    },

    onOpenPromo() {
      this.stepName = "promo";
    },

    onPromoApplied(payload) {
      this.promocodeId = (payload && payload.promocode_id) || "";
      this.promocodeVoucherId = (payload && payload.promocode_voucher_id) || "";
      this.promocodeCode = (payload && payload.promocode_code) || "";
      this.promocodeName = (payload && payload.promocode_name) || "";
      this.promocodeDiscount = (payload && payload.discount_amount) || 0;
      this.stepName = "review";
    },

    onPromoRemoved() {
      this.clearPromo();
      this.stepName = "review";
    },

    clearPromo() {
      this.promocodeId = "";
      this.promocodeVoucherId = "";
      this.promocodeCode = "";
      this.promocodeName = "";
      this.promocodeDiscount = 0;
    },

    /**
     * SH016 — the profile menu's entries.
     *
     * "Order" is the booking itself: it drops the customer back on the service picker,
     * step 4, where an appointment starts. Profile, Awards and Bookings are detours, so
     * where they came from is remembered for their Back arrow. SH022's Log out ends the
     * flow instead.
     */
    onProfileNavigate(target) {
      if (target === "order") {
        this.stepBeforeService = "welcome";
        // SH020 — same reason as onBookAnother(): reached from the landing page's history,
        // there is no outlet yet, and the service picker has no menu set to list.
        this.stepName = this.branchCode ? "service" : "location";
        return;
      }

      if (target === "logout") {
        this.onLogout();
        return;
      }

      // SH044 — the vote is about one sale channel's team, so it cannot be answered before
      // the customer has picked an outlet. Opened from the landing page's history, there is
      // none yet, and the outlet list is where that is decided.
      if (target === "vote" && !this.branchCode) {
        this.returnStep = "location";
        this.stepName = "location";
        return;
      }

      if (!DETOUR_STEPS.includes(target)) {
        return;
      }

      // Hopping straight from one detour to another must not overwrite the screen behind
      // both of them with the detour the customer is leaving.
      if (!DETOUR_STEPS.includes(this.stepName)) {
        this.returnStep = this.stepName;
      }

      this.stepName = target;
    },

    /**
     * SH024 — the profile screen saved a new name or email.
     *
     * The avatar's initials and every screen's greeting are drawn from `user`, and the
     * stored member blob is what onOutletSelected() reads on the way back in, so both are
     * patched rather than left showing the name the customer just changed.
     */
    onProfileUpdated(payload) {
      if (!payload) {
        return;
      }

      this.user = {
        ...(this.user || {}),
        name: payload.name,
        last_name: payload.last_name,
        email: payload.email,
      };

      try {
        const member = TokenService.getMember();

        if (member?.data?.user) {
          member.data.user = { ...member.data.user, ...this.user };
          TokenService.setMember(member);
        }
      } catch (error) {
        // The blob is a convenience for the next visit, not the source of truth for this
        // one — a malformed one must not take the save down with it.
      }
    },

    /**
     * SH020 — the top-bar arrow on the landing page.
     *
     * The history's own Back opens the latest appointment, so this only has to remember
     * the landing page for the case where there is no appointment to open.
     */
    onOpenBookingsFromLanding() {
      this.returnStep = "location";
      this.stepName = "bookings";
    },

    // SH010 — "View my bookings" on the ticket. The ticket is what Back returns to.
    onOpenBookings() {
      this.returnStep = "created";
      this.stepName = "bookings";
    },

    // SH010 — the appointment exists, so the ticket takes over.
    onBookingCreated(booking) {
      this.booking = booking || null;
      this.stepName = "created";
    },

    onBookingCancelled(booking) {
      // The screen restates itself from its own state; this only keeps the container in
      // step, so "View my bookings" and a later return do not show a stale ticket.
      if (booking) {
        this.booking = booking;
      }
    },

    // SH010 — tapping a row in "My bookings" opens that appointment's ticket. Same screen
    // the customer saw when they made it, so cancelling works from history too.
    onOpenBooking(booking) {
      if (!booking) {
        return;
      }

      this.booking = booking;
      this.stepName = "created";
    },

    /**
     * SH017 — "Vote for stylist" on a past appointment.
     *
     * A detour off the history list, not a step of the booking flow: Back returns to
     * "bookings", and nothing about the appointment being made is touched.
     */
    onRateBooking(booking) {
      if (!booking) {
        return;
      }

      this.ratingBooking = booking;
      this.stepName = "rating";
    },

    /**
     * The vote is in. Back to the history, which re-reads itself on mount — so the row
     * that was inviting a vote comes back showing the stars that were given.
     */
    onBookingRated() {
      this.ratingBooking = null;
      this.stepName = "bookings";
    },

    /**
     * SH022 — Log out, from the profile menu's own confirmation.
     *
     * The store action is the whole member session: it drops the blob TokenService wrote
     * at login, which is what isMemberSignedIn() reads on the way back in. MOO's
     * Profile.vue calls localStorage.clear() for this instead, and that would take
     * MERCHANT_ID and BRANCH_INFO with it — the outlet list this returns to needs both.
     *
     * Step 1 is the landing: the customer asked to be signed out, so the outlet they had
     * picked and the appointment they were part-way through go with the session.
     */
    onLogout() {
      this.$store.dispatch("auth/logout");

      this.user = null;
      this.phoneNumber = "";
      this.phoneCode = "65";
      this.branchCode = "";
      this.branchName = "";
      this.branchId = 0;
      this.addressText = "";
      this.postalCode = "";
      this.destinationLink = "";
      this.selectedServices = [];
      this.selectedDate = "";
      this.selectedDateText = "";
      this.selectedDay = null;
      this.durationMin = 0;
      this.selectedTime = "";
      this.selectedTimeText = "";
      this.selectedStaffId = "";
      this.selectedStaffName = "";
      this.clearPromo();
      this.booking = null;
      this.ratingBooking = null;
      this.stepBeforeService = "welcome";
      this.returnStep = "service";
      // SH026 — the outlet is gone with the session, and its flow setting with it.
      this.bookingFlow = FLOW_DATE_FIRST;
      this.flowOrder = "date";

      this.stepName = "location";
    },

    /**
     * SH010 — "Book another appointment".
     *
     * Starts again at the service picker rather than at the outlet: the customer has just
     * booked at this salon, and making them re-pick the same outlet would be busywork. What
     * they chose last time is cleared, because none of it applies to a new appointment.
     */
    onBookAnother() {
      this.selectedServices = [];
      this.selectedDate = "";
      this.selectedDateText = "";
      this.selectedDay = null;
      this.durationMin = 0;
      this.selectedTime = "";
      this.selectedTimeText = "";
      this.selectedStaffId = "";
      this.selectedStaffName = "";
      this.booking = null;
      this.clearPromo();
      this.stepBeforeService = "welcome";
      // SH026 — a new appointment starts on the order this channel is set to, even if the
      // last one was booked the other way round on a channel that lets the customer switch.
      this.flowOrder = this.bookingFlow === FLOW_STAFF_FIRST ? "staff" : "date";

      // SH020 — the history can now be reached from the landing page, so a customer can
      // arrive at a ticket without ever having picked an outlet. The service picker lists
      // one outlet's menu set, so with no outlet it has nothing to show: send them to
      // step 1 to choose one first.
      this.stepName = this.branchCode ? "service" : "location";
    },
  },
};
</script>
